import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MobileStepper from '@mui/material/MobileStepper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { FirstTimeSigninProps } from './types';
import { SocialIcon } from 'react-social-icons';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { getCurrentUser, updateUserAttributes } from 'aws-amplify/auth';
import { useAuth } from '../../hooks';
import { useAvatar } from '../../hooks/useAvatar';
import { useAuthStore } from '../../stores/authStore';
import { useAvatarStore } from '../../stores/avatarStore';
import UserService from '../../service/UserService';
import ApiService from '../../service/ApiService';
import { apiGatewayUrlParam } from '../../config/ServiceConfig';
import CircularProgress from '@mui/material/CircularProgress';

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const socialIconStyle = {
  height: 35,
  width: 35,
  marginLeft: 15,
  marginRight: 15
};

const socialMediaUsernameContainer = {
  overflow: "hidden", 
  textOverflow: "ellipsis", 
  width: '11rem'
}

const images = [
  {
    label: "Welcome to the Fam!",
    imgPath: process.env.PUBLIC_URL + '/welcome_1.png',
    stepBody: () => {
      return (
        <Typography>
          {"You just claimed you'r first Kandi! Kandi Love "
           + "is a free Kandi App built for the rave community. "
           + "Here is a quick walkthrough of what you can do with Kandi Love!"}
        </Typography>
      )
    }
  },
  {
    label: 'Register your Kandi',
    imgPath: process.env.PUBLIC_URL + '/welcome_2.png',
    stepBody: () => {
      return (
        <Typography>
          { "Add Kandi you've created to  your collection, attach a Unity Bead, "
     + "and scan/register the QR code on the Unity Bead to start the Kandi's journey."}
        </Typography>
      )
    }
  },
  {
    label: 'Trade and Scan',
    imgPath: process.env.PUBLIC_URL + '/welcome_3.png',
    stepBody: () => {
      return (
        <Typography>
          {  "Trade your kandi, and when your friend scans the QR code on the Unity Bead "
     + "they will be taken to the dedicated Kandi page where they can claim the Kandi and add it "
     + "to their own collection."}
        </Typography>
      )
    }
  },
  {
    label: 'Keep in touch',
    imgPath: process.env.PUBLIC_URL + '/welcome_4.png',
    stepBody: () => {
      return (
        <Typography>
          {  "The history of Kandi ownership is found on the dedicated Kandi page so you "
     + "can find the previous owner, view their profile, and keep in touch."}
        </Typography>
      )
    }
  },
  {
    label: 'Thank you for being here!',
    imgPath: process.env.PUBLIC_URL + '/welcome_5.png',
    stepBody: () => {
      return (
        <Box>
           <Typography>
          { "Stay up to date on everything we do by following us on Instagram!"}
          </Typography>
          <Grid container direction="row" alignItems="center" margin="10px">
            <Grid item>
                <SocialIcon url={"https://www.instagram.com/kandilovellc/"} style={socialIconStyle}/>
            </Grid>
            <Grid item>
                <div style={socialMediaUsernameContainer}> 
                    <Typography noWrap variant="body2">
                        <Link href="https://www.instagram.com/kandilovellc/" target="_blank">
                          {"@KandiLoveLLC"}
                        </Link>
                    </Typography>
                </div>
            </Grid>
          </Grid>
          <Grid container direction="row" alignItems="center" margin="10px">
            <Grid item>
              <IconButton
              style={socialIconStyle}
              href="https://kandilove.com/collections/unity-beads"
              target="_blank"
            >
              <Avatar src={process.env.PUBLIC_URL + '/unity_bead_icon.png'}/>
            </IconButton>
            </Grid>
            <Grid item>
                <div style={socialMediaUsernameContainer}> 
                    <Typography noWrap variant="body2">
                        <Link href="https://kandilove.com/collections/unity-beads" target="_blank">
                          {"Shop Unity Beads"}
                        </Link>
                    </Typography>
                </div>
            </Grid>
          </Grid>
          <Grid container direction="row" alignItems="center" margin="10px">
            <Grid item>
              <IconButton
              style={socialIconStyle}
              href="https://blog.kandilove.com/2024/07/03/how-to-make-a-unity-bead-at-home-for-free/"
              target="_blank"
            >
              <Avatar src={process.env.PUBLIC_URL + '/unity_bead_icon.png'}/>
            </IconButton>
            </Grid>
            <Grid item>
                <div style={socialMediaUsernameContainer}> 
                    <Typography noWrap variant="body2">
                        <Link href="https://blog.kandilove.com/2024/07/03/how-to-make-a-unity-bead-at-home-for-free/" target="_blank">
                          {"FREE DIY Unity Beads"}
                        </Link>
                    </Typography>
                </div>
            </Grid>
          </Grid>
        </Box>
      )
    }
  }
];

  const FirstTimeSigninModal: React.FunctionComponent<FirstTimeSigninProps> = (props) => {
    const theme = useTheme();
    const { user } = useAuth();
    const { handle: currentHandle } = useAvatar();
    const { updateAvatar } = useAvatarStore();
    const [activeStep, setActiveStep] = React.useState(0);
    const [showUsernameSelection, setShowUsernameSelection] = React.useState(false);
    const [isCheckingAccounts, setIsCheckingAccounts] = React.useState(false);
    const [username, setUsername] = React.useState('');
    const [usernameError, setUsernameError] = React.useState('');
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    
    // Extract stable values to prevent infinite loops
    const userEmail = user?.email;
    const userIdentities = user?.attributes?.identities;
    
    // Helper function to detect auto-generated usernames
    const isAutoGeneratedUsername = React.useCallback((username: string) => {
      if (!username) return true;
      
      return (
        username.startsWith('Google_') ||
        username.includes('@') ||
        /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(username) || // UUID pattern
        username.length > 20 || // Cognito auto-generated are typically long
        /^[a-f0-9]{20,}$/i.test(username) // Long hex strings
      );
    }, []);

    // Check if user is a Google sign-in user
    const isSocialUser = React.useMemo(() => {
      if (!userIdentities) return false;
      try {
        const identities = JSON.parse(userIdentities);
        return identities.some((identity: any) => identity.providerName === 'Google');
      } catch {
        return false;
      }
    }, [userIdentities]);

    // Check if user is a linked Google account by checking if effective user ID differs from Cognito username
    const isLinkedGoogleAccount = React.useMemo(() => {
      if (!isSocialUser || !user?.username) return false;
      
      // For linked accounts, the user.id and user.username should be the effective user ID (canonical user)
      // while the Cognito username would be the Google_ prefixed one
      // If they're different, it means this is a linked account
      const cognitoUsername = user.username || user.id;
      const isAutoGenerated = isAutoGeneratedUsername(cognitoUsername);
      
      // If it's a social user with an auto-generated username, check if we have profile data
      // indicating this is a linked account (has appUserId)
      return isSocialUser && isAutoGenerated;
    }, [isSocialUser, user?.username, user?.id, isAutoGeneratedUsername]);

    // Handle auto-population when user signs in
    React.useEffect(() => {
        const handleUserSignIn = async () => {
            if (props.open && user && !isCheckingAccounts) {
                setIsCheckingAccounts(true);
                
                try {
                    // Check if the current Cognito username is auto-generated
                    const cognitoUsername = user.username || user.id;
                    const isAutoGenerated = isAutoGeneratedUsername(cognitoUsername);
                    
                    console.log('🔍 FirstTimeSignin Debug - Username check:', {
                        cognitoUsername,
                        isAutoGenerated,
                        isSocialUser,
                        isLinkedGoogleAccount,
                        onboardingCompleted: false
                    });

                    // For non-auto-generated usernames, auto-complete onboarding
                    if (!isAutoGenerated) {
                        console.log('🔍 FirstTimeSignin Debug - Auto-populating username:', cognitoUsername);
                        
                        try {
                            // Set the display name in avatar data and complete onboarding
                            const userService = new UserService();
                            await userService.setUserAvatar(cognitoUsername, { handle: cognitoUsername });
                            const apiService = new ApiService(apiGatewayUrlParam);
                            await apiService.completeOnboarding(cognitoUsername);
                            
                            // Update the local avatar store with the handle
                            updateAvatar({
                                handle: cognitoUsername,
                                bio: null,
                                pronouns: null,
                                location: null
                            });
                            
                            console.log('🔍 FirstTimeSignin Debug - Auto-population completed, closing modal');
                            props.onClose();
                            return;
                        } catch (error) {
                            console.error('Error auto-populating username:', error);
                            // Fall through to show username selection
                        }
                    }

                    // Show username selection for auto-generated usernames
                    console.log('🔍 FirstTimeSignin Debug - Showing username selection for auto-generated username');
                    setShowUsernameSelection(true);
                    // Pre-populate with suggestion from email or a clean version
                    const suggestedUsername = userEmail?.split('@')[0] || '';
                    setUsername(suggestedUsername);
                    
                } catch (error) {
                    console.error('Error in handleUserSignIn:', error);
                    // Fallback to username selection
                    setShowUsernameSelection(true);
                    const suggestedUsername = userEmail?.split('@')[0] || '';
                    setUsername(suggestedUsername);
                } finally {
                    setIsCheckingAccounts(false);
                }
            }
        };

        handleUserSignIn();
    }, [props.open, user, userEmail, isAutoGeneratedUsername, props.onClose, isCheckingAccounts]);

    const maxSteps = images.length;
  
    const handleNext = () => {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };
  
    const handleBack = () => {
      setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };
  
    const handleStepChange = (step: number) => {
      setActiveStep(step);
    };

    const handleUsernameSubmit = async () => {
      if (!username.trim()) {
        setUsernameError('Username is required');
        return;
      }

      // Validate username format
      const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
      if (!usernameRegex.test(username.trim())) {
        setUsernameError('Username must be 3-20 characters and contain only letters, numbers, and underscores');
        return;
      }

      setIsSubmitting(true);
      try {
        // Check username availability only if it's different from current handle
        const trimmedUsername = username.trim();
        const isChangingHandle = trimmedUsername !== currentHandle;
        
        if (isChangingHandle) {
          const apiService = new ApiService(apiGatewayUrlParam);
          const availabilityCheck = await apiService.checkUsernameAvailability(trimmedUsername);
          
          if (!availabilityCheck.available) {
            setUsernameError(availabilityCheck.message || 'Username is already taken. Please choose another.');
            setIsSubmitting(false);
            return;
          }
        }
        
        if (user){
          const userService = new UserService();
          await userService.setUserAvatar(user.username, { handle: username.trim() });
          console.log('🔍 FirstTimeSignin Debug - setAvatar completed successfully');
          
          const apiService = new ApiService(apiGatewayUrlParam);
          await apiService.completeOnboarding(user.username);
          console.log('🔍 FirstTimeSignin Debug - Onboarding completed successfully');

          // Update the local avatar store with the new handle
          updateAvatar({
            handle: username.trim(),
            bio: null,
            pronouns: null,
            location: null
          });
          console.log('🔍 FirstTimeSignin Debug - Local avatar store updated with new handle');

          setShowUsernameSelection(false);
          setUsernameError('');
        }
      } catch (error) {
        console.error('Error updating username:', error);
        setUsernameError('Failed to update username. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleClose = async () => {
      if (isSocialUser && username && user) {
        const cognitoUsername = user.username || user.id;
        const userService = new UserService();
        await userService.setUserAvatar(cognitoUsername, { handle: username });
        const apiService = new ApiService(apiGatewayUrlParam);
        await apiService.completeOnboarding(cognitoUsername);
        
        // Update the local avatar store with the handle
        updateAvatar({
          handle: username,
          bio: null,
          pronouns: null,
          location: null
        });
      }
      props.onClose();
    };

    const closeText = () => {
      if (activeStep === maxSteps - 1) {
        return "Close"
      } else {
        return "Skip"
      }
    }

    // Show loading state while checking accounts
    if (isCheckingAccounts) {
      return (
        <Dialog open={props.open}>
          <DialogContent>
            <Box sx={{ maxWidth: 400, flexGrow: 1, p: 3, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Setting up your account...
              </Typography>
            </Box>
          </DialogContent>
        </Dialog>
      );
    }
  
    // Show username selection for users with auto-generated usernames
    if (showUsernameSelection) {
      return (
        <Dialog open={props.open}>
          <DialogContent>
            <Box sx={{ maxWidth: 400, flexGrow: 1, p: 3 }}>
              <Typography variant="h5" gutterBottom align="center">
                Choose Your Username
              </Typography>
              <Typography variant="body2" gutterBottom align="center" sx={{ mb: 3 }}>
                Welcome! Please choose a username for your Kandi Love account.
              </Typography>
              <TextField
                fullWidth
                label="Username"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setUsernameError('');
                }}
                error={!!usernameError}
                helperText={usernameError || 'This will be your unique username on Kandi Love'}
                disabled={isSubmitting}
                sx={{ mb: 3 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              color="primary" 
              variant="contained"
              onClick={handleUsernameSubmit}
              disabled={!username.trim() || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Continue'}
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    return (
      <Dialog
        open={props.open}
      >
        <DialogContent>
          <Box sx={{ maxWidth: 400, flexGrow: 1 }}>
            <AutoPlaySwipeableViews
              interval={10000}
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={activeStep}
              onChangeIndex={handleStepChange}
              enableMouseEvents
              autoplay={!(activeStep === maxSteps - 1)}
            >
              {images.map((step, index) => (
                <div key={step.label}>
                  <Paper
                    square
                    elevation={0}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      pl: 2,
                      bgcolor: '#000000',
                      minHeight: '550px',
                      overflow: 'hidden'
                    }}
                  >
                    {Math.abs(activeStep - index) <= 2 ? (
                      <Box
                        component="img"
                        sx={{
                          display: 'block',
                          maxWidth: 400,
                          overflow: 'hidden',
                          width: '100%',
                        }}
                        src={step.imgPath}
                        alt={step.label}
                      />
                    ) : null}
                    <Typography 
                      variant='h5'
                      color={theme.palette.primary.main}
                    >
                      {images[activeStep].label}
                    </Typography>
                    {images[activeStep].stepBody()}
                  </Paper>
                </div>
              ))}
            </AutoPlaySwipeableViews>
            
            <MobileStepper
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              nextButton={
                <Button
                  size="small"
                  onClick={handleNext}
                  disabled={activeStep === maxSteps - 1}
                >
                  Next
                  {theme.direction === 'rtl' ? (
                    <KeyboardArrowLeft />
                  ) : (
                    <KeyboardArrowRight />
                  )}
                </Button>
              }
              backButton={
                <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
                  {theme.direction === 'rtl' ? (
                    <KeyboardArrowRight />
                  ) : (
                    <KeyboardArrowLeft />
                  )}
                  Back
                </Button>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
            <Button 
                color="primary" 
                onClick={handleClose}
            >
                {closeText()}
            </Button>
        </DialogActions>
      </Dialog>
    );
  }
  
  export default FirstTimeSigninModal;
